import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Pencil, Calendar } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import RichText from "@/components/RichText";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function toDateInput(iso) {
  if (!iso) return "";
  // accept YYYY-MM-DD or full ISO; coerce to YYYY-MM-DD
  return String(iso).slice(0, 10);
}

export default function AdminTermine() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/termine");
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save(t) {
    if (!t.title?.trim()) { toast.error("Titel ist erforderlich"); return; }
    if (!t.date) { toast.error("Datum ist erforderlich"); return; }
    const payload = { date: t.date, title: t.title.trim(), body: t.body || "", active: t.active !== false };
    try {
      if (t.__new) {
        await api.post("/termine", payload);
        toast.success("Termin angelegt");
      } else {
        await api.put(`/termine/${t.id}`, payload);
        toast.success("Gespeichert");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function del(id) {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try { await api.delete(`/termine/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="admin-termine">
      <div className="flex items-start justify-between mb-1">
        <h1 className="font-semibold text-3xl">Termine</h1>
        <button
          onClick={() => setEditing({ __new: true, date: today, title: "", body: "", active: true })}
          data-testid="termin-new"
          className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]"
        >
          <Plus className="h-4 w-4" /> Neuer Termin
        </button>
      </div>
      <p className="text-sm text-neutral-500 mb-8">Baustellen-Termine, Sperrungen und Meilensteine chronologisch pflegen.</p>

      <div className="bg-white border border-brand-line">
        {loading ? (
          <p className="p-8 text-sm text-neutral-500">Lade…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-sm text-neutral-500">Noch keine Termine angelegt.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3 font-normal w-40">Datum</th>
                <th className="text-left px-5 py-3 font-normal">Titel</th>
                <th className="text-left px-5 py-3 font-normal w-24">Status</th>
                <th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const past = toDateInput(t.date) < today;
                return (
                  <tr key={t.id} className="border-t border-brand-line" data-testid={`termin-row-${t.id}`}>
                    <td className="px-5 py-3 align-top text-neutral-600">
                      <div className="inline-flex items-center gap-2">
                        <Calendar className={`h-3.5 w-3.5 ${past ? "text-neutral-400" : "text-[hsl(var(--brand-green-strong))]"}`} />
                        {formatDate(t.date)}
                      </div>
                      {past && <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-0.5">Vergangen</div>}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <p className="font-medium text-neutral-900">{t.title}</p>
                      {t.body && (
                        <div className="text-xs text-neutral-500 line-clamp-2 mt-1 max-w-xl">
                          <RichText html={t.body} />
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 align-top">
                      <span className={`text-xs ${t.active ? "text-[hsl(var(--brand-green-strong))]" : "text-neutral-400"}`}>
                        {t.active ? "Aktiv" : "Inaktiv"}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => setEditing(t)} data-testid={`edit-termin-${t.id}`} className="text-neutral-500 hover:text-[hsl(var(--brand-green-strong))]"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(t.id)} data-testid={`delete-termin-${t.id}`} className="text-neutral-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && <TerminEditor termin={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TerminEditor({ termin, onClose, onSave }) {
  const [t, setT] = useState({ ...termin, date: toDateInput(termin.date) });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="termin-editor">
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-semibold text-2xl">{termin.__new ? "Neuer Termin" : "Termin bearbeiten"}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block sm:col-span-1">
              <span className="block text-xs text-neutral-500 mb-1.5">Datum *</span>
              <input
                type="date"
                value={t.date}
                onChange={(e) => setT({ ...t, date: e.target.value })}
                data-testid="termin-date"
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-xs text-neutral-500 mb-1.5">Titel / Überschrift *</span>
              <input
                value={t.title}
                onChange={(e) => setT({ ...t, title: e.target.value })}
                data-testid="termin-title"
                placeholder="z. B. Vollsperrung Hornsche Straße"
                className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-xs text-neutral-500 mb-1.5">Information / Beschreibung</span>
            <RichTextEditor
              value={t.body}
              onChange={(html) => setT({ ...t, body: html })}
              minHeight={200}
              placeholder="Details zum Termin, z. B. Umleitungen, Bauabschnitte, Ansprechpartner…"
              testid="termin-body"
            />
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={t.active !== false} onChange={(e) => setT({ ...t, active: e.target.checked })} />
            Auf der Webseite anzeigen
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="border border-brand-line px-5 py-2.5 text-sm hover:bg-neutral-50">Abbrechen</button>
          <button onClick={() => onSave(t)} data-testid="termin-save" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-6 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
            <Save className="h-4 w-4" /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
